import { createContext, useContext, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

/**
 * OOR Report lifecycle (SCRUM-191).
 *
 * The "Generate report" / "Mark as Submitted" / "Close Report" / "New report"
 * buttons live in the My Tasks tab row (src/pages/patient-page/dailies/index.js),
 * while the report itself (filters, table, share/download, and the lifecycle
 * popups) lives in the OORReport panel. They are sibling components under
 * MyTasks, so this React Context is the single source of truth they both read
 * and write, including the open-state of the lifecycle popups (which are
 * triggered from the tab row but rendered inside the panel).
 *
 * Status machine:
 *   idle ──generate──▶ generated ──mark submitted──▶ submitted
 *                          │
 *                          └──close─▶ closed (or submitted, via
 *                                     "Close & Mark as Submitted")
 *   submitted | closed ──new report──▶ idle
 *
 * Generate is backed by the real backend (POST /out-of-range/reports/generate/,
 * SCRUM-191/232 handoff): the button asks the OOR panel to run the API call via
 * a registered handler (the panel owns the filter state the payload needs) and
 * the context adopts the returned report_id + meta on success. `reportMeta` is
 * the filter snapshot captured at generation time.
 */

export const REPORT_STATUS = {
  IDLE: "idle",
  GENERATED: "generated",
  SUBMITTED: "submitted",
  CLOSED: "closed",
};

export const SUBMIT_MESSAGES = {
  DIRECT: "This report will be marked as submitted.",
  POST_SHARE:
    "This report has been successfully shared. Would you like to mark it as Submitted?",
  POST_DOWNLOAD:
    "This report has been downloaded successfully. Would you like to mark it as Submitted?",
};

const ReportLifecycleContext = createContext(null);

const CLOSED_SUBMIT_PROMPT = { open: false, message: "", cancelLabel: "Cancel" };

export const ReportLifecycleProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState(REPORT_STATUS.IDLE);
  const [reportMeta, setReportMeta] = useState(null);
  // Mark-Submitted-style prompt (direct action + post-share / post-download).
  const [submitPrompt, setSubmitPrompt] = useState(CLOSED_SUBMIT_PROMPT);
  // Close Report confirm dialog.
  const [closeReportOpen, setCloseReportOpen] = useState(false);
  // SCRUM-138: in-place History view toggle (rendered inside the OOR panel so
  // the report's filters/state are preserved while history is open).
  const [showHistory, setShowHistory] = useState(false);
  // Id the lifecycle events (Generated/Downloaded/Shared/Submitted/Closed) are
  // grouped under for one generated report. Set from the backend report_id
  // returned by POST generate/.
  const [currentReportId, setCurrentReportId] = useState(null);
  // True while the generate API call is in flight (button spinner + reentry guard).
  const [isGenerating, setIsGenerating] = useState(false);
  // Set by the panel when the current report can't be submitted (SCRUM-790:
  // an Extended Threshold draft with 0 patient rows); disables Mark as Submitted.
  const [submitBlocked, setSubmitBlocked] = useState(false);
  // Set by the panel when the active report's preview has no records, so
  // there's nothing to generate; disables Generate report.
  const [generateBlocked, setGenerateBlocked] = useState(false);
  // The report view the panel is showing (SCRUM-790). An Extended Threshold
  // Draft has to be submitted or closed, so "New report" is hidden for it.
  const [reportView, setReportView] = useState(null);
  // The Generate button (tab row) has no access to the OOR panel's filter
  // state, so the panel registers the async handler that actually calls the
  // backend. A ref (not state) so registration never re-renders the tree.
  const generateHandlerRef = useRef(null);

  const value = useMemo(() => {
    const registerGenerateHandler = (fn) => {
      generateHandlerRef.current = fn;
    };
    // Async: runs the panel-registered API call; only flips to GENERATED when
    // the backend confirms. The handler returns { reportId, meta } on success
    // and null on failure (it surfaces its own error toast).
    const generate = async () => {
      const handler = generateHandlerRef.current;
      if (!handler) return; // panel not mounted; nothing to generate
      setIsGenerating(true);
      try {
        const result = await handler();
        if (result?.reportId) {
          setReportMeta(result.meta || null);
          setCurrentReportId(result.reportId);
          setStatus(REPORT_STATUS.GENERATED);
          queryClient.invalidateQueries(["oor-report-history"], { exact: false });
        }
      } finally {
        setIsGenerating(false);
      }
    };
    // SCRUM-790: pick up a Draft that already exists on the backend (the
    // location's open Extended Threshold Draft) without generating a new one.
    const adoptReport = ({ reportId, meta } = {}) => {
      if (!reportId) return;
      setReportMeta(meta || null);
      setCurrentReportId(reportId);
      setSubmitPrompt(CLOSED_SUBMIT_PROMPT);
      setCloseReportOpen(false);
      setStatus(REPORT_STATUS.GENERATED);
    };
    const markSubmitted = () => setStatus(REPORT_STATUS.SUBMITTED);
    const close = ({ alsoSubmit } = {}) =>
      setStatus(alsoSubmit ? REPORT_STATUS.SUBMITTED : REPORT_STATUS.CLOSED);
    const resetReport = () => {
      setStatus(REPORT_STATUS.IDLE);
      setReportMeta(null);
      setCurrentReportId(null);
      setSubmitPrompt(CLOSED_SUBMIT_PROMPT);
      setCloseReportOpen(false);
    };

    const openHistory = () => setShowHistory(true);
    const closeHistory = () => setShowHistory(false);

    const openSubmitPrompt = ({
      message = SUBMIT_MESSAGES.DIRECT,
      cancelLabel = "Cancel",
    } = {}) => setSubmitPrompt({ open: true, message, cancelLabel });
    const dismissSubmitPrompt = () => setSubmitPrompt(CLOSED_SUBMIT_PROMPT);
    const openCloseReport = () => setCloseReportOpen(true);
    const dismissCloseReport = () => setCloseReportOpen(false);

    return {
      status,
      reportMeta,
      setReportMeta,
      // Convenience flags.
      isIdle: status === REPORT_STATUS.IDLE,
      isGenerated: status === REPORT_STATUS.GENERATED,
      isLocked: status !== REPORT_STATUS.IDLE, // filters frozen once generated
      isFinal:
        status === REPORT_STATUS.SUBMITTED || status === REPORT_STATUS.CLOSED,
      // Transitions.
      generate,
      isGenerating,
      registerGenerateHandler,
      adoptReport,
      markSubmitted,
      close,
      resetReport,
      submitBlocked,
      setSubmitBlocked,
      generateBlocked,
      setGenerateBlocked,
      reportView,
      setReportView,
      // Popup coordination (triggered from the tab row, rendered in the panel).
      submitPrompt,
      openSubmitPrompt,
      dismissSubmitPrompt,
      closeReportOpen,
      openCloseReport,
      dismissCloseReport,
      // SCRUM-138 history.
      currentReportId,
      showHistory,
      openHistory,
      closeHistory,
    };
  }, [queryClient, status, reportMeta, submitPrompt, closeReportOpen, showHistory, currentReportId, isGenerating, submitBlocked, generateBlocked, reportView]);

  return (
    <ReportLifecycleContext.Provider value={value}>
      {children}
    </ReportLifecycleContext.Provider>
  );
};

/**
 * Read the report lifecycle. Returns a safe no-op shell when used outside the
 * provider so the OORReport panel still renders in isolation (e.g. tests).
 */
export const useReportLifecycle = () => {
  const ctx = useContext(ReportLifecycleContext);
  if (ctx) return ctx;
  return {
    status: REPORT_STATUS.IDLE,
    reportMeta: null,
    setReportMeta: () => {},
    isIdle: true,
    isGenerated: false,
    isLocked: false,
    isFinal: false,
    generate: () => {},
    isGenerating: false,
    registerGenerateHandler: () => {},
    adoptReport: () => {},
    markSubmitted: () => {},
    close: () => {},
    resetReport: () => {},
    submitBlocked: false,
    setSubmitBlocked: () => {},
    generateBlocked: false,
    setGenerateBlocked: () => {},
    reportView: null,
    setReportView: () => {},
    submitPrompt: CLOSED_SUBMIT_PROMPT,
    openSubmitPrompt: () => {},
    dismissSubmitPrompt: () => {},
    closeReportOpen: false,
    openCloseReport: () => {},
    dismissCloseReport: () => {},
    currentReportId: null,
    showHistory: false,
    openHistory: () => {},
    closeHistory: () => {},
  };
};
